import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import {
  CheckCircle,
  XCircle,
  User,
  Building2,
  Calendar,
  Phone,
  Globe,
  Stethoscope,
  AlertTriangle,
  CreditCard,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { EligibilityCheckResponse } from '../services/api';
import {
  formatDateTimeInAgencyZone,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import {
  formatEligibilityErrorForDisplay,
  type StediAAAError,
} from '../utils/eligibilityErrorMessages';
import { PermissionSlug } from '../constants/featureFlags';
import { useStore } from '../store';

interface EligibilityDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  eligibilityResult: EligibilityCheckResponse | null;
}

// Types for the raw Stedi response
interface StediSubscriber {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  memberId?: string;
  dateOfBirth?: string;
  gender?: string;
  groupNumber?: string;
  planNumber?: string;
  groupDescription?: string;
  address?: {
    address1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
}

interface StediPayer {
  name?: string;
  payorIdentification?: string;
  contactInformation?: {
    contacts?: Array<{
      communicationMode?: string;
      communicationNumber?: string;
    }>;
  };
}

interface StediPlanInfo {
  groupNumber?: string;
  groupDescription?: string;
  planNumber?: string;
}

interface StediPlanDateInfo {
  // Raw Stedi format
  plan?: string;
  eligibility?: string;
  // Parsed/stored format
  planBegin?: string;
  planEnd?: string;
  eligibilityBegin?: string;
  eligibilityEnd?: string;
}

interface StediBenefitRelatedEntity {
  entityName?: string;
  entityIdentifier?: string;
  entityFirstname?: string;
  entityMiddlename?: string;
  entityIdentificationValue?: string;
  address?: {
    address1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  contactInformation?: {
    contacts?: Array<{
      communicationMode?: string;
      communicationNumber?: string;
    }>;
  };
  providerInformation?: {
    providerCode?: string;
    referenceIdentification?: string;
  };
}

interface StediBenefitInfo {
  code?: string;
  name?: string;
  serviceTypes?: string[];
  serviceTypeCodes?: string[];
  coverageLevel?: string;
  coverageLevelCode?: string;
  benefitAmount?: string;
  benefitPercent?: string;
  timeQualifier?: string;
  timeQualifierCode?: string;
  insuranceType?: string;
  insuranceTypeCode?: string;
  inPlanNetworkIndicator?: string;
  inPlanNetworkIndicatorCode?: string;
  planCoverage?: string;
  authOrCertIndicator?: string;
  additionalInformation?: Array<{ description?: string }>;
  benefitsRelatedEntity?: StediBenefitRelatedEntity;
  benefitsRelatedEntities?: StediBenefitRelatedEntity[];
  benefitsDateInformation?: {
    // Raw Stedi format
    plan?: string;
    benefit?: string;
    // Parsed/stored format
    planBegin?: string;
    planEnd?: string;
    benefitBegin?: string;
    benefitEnd?: string;
    periodStart?: string;
    periodEnd?: string;
    coordinationOfBenefits?: string;
  };
  benefitsAdditionalInformation?: {
    planNumber?: string;
    planNetworkIdNumber?: string;
    planNetworkDescription?: string;
    hicNumber?: string;
  };
}

interface StediRawResponse {
  subscriber?: StediSubscriber;
  payer?: StediPayer;
  planInformation?: StediPlanInfo;
  planDateInformation?: StediPlanDateInfo;
  benefitsInformation?: StediBenefitInfo[];
  planStatus?: Array<{
    status?: string;
    statusCode?: string;
    planDetails?: string;
    serviceTypeCodes?: string[];
  }>;
  provider?: {
    npi?: string;
    providerName?: string;
    organizationName?: string;
  };
  encounter?: {
    serviceTypeCodes?: string[];
  };
  errors?: unknown[];
}

// Service Type Code to Name mapping
const SERVICE_TYPE_NAMES: Record<string, string> = {
  '1': 'Medical Care',
  '2': 'Surgical',
  '3': 'Consultation',
  '23': 'Diagnostic Dental',
  '24': 'Periodontics',
  '25': 'Restorative',
  '26': 'Endodontics',
  '27': 'Maxillofacial Prosthetics',
  '28': 'Adjunctive Dental Services',
  '30': 'Health Benefit Plan Coverage',
  '33': 'Chiropractic',
  '35': 'Dental Care',
  '36': 'Dental Crowns',
  '37': 'Dental Accident',
  '38': 'Orthodontics',
  '39': 'Prosthodontics',
  '40': 'Oral Surgery',
  '41': 'Routine (Preventive) Dental',
  '42': 'Home Health Care',
  '45': 'Hospice',
  '47': 'Hospital',
  '48': 'Hospital - Inpatient',
  '49': 'Hospital - Room and Board',
  '50': 'Hospital - Outpatient',
  '51': 'Hospital - Emergency Accident',
  '52': 'Hospital - Emergency Medical',
  '53': 'Hospital - Ambulatory Surgical',
  '54': 'Long Term Care',
  '67': 'Smoking Cessation',
  '69': 'Maternity',
  '73': 'Diagnostic Medical',
  '76': 'Dialysis',
  '83': 'Infertility',
  '86': 'Emergency Services',
  '88': 'Pharmacy',
  '96': 'Professional (Physician)',
  '98': 'Professional (Physician) Visit - Office',
  A4: 'Psychiatric',
  A5: 'Psychiatric - Room and Board',
  A6: 'Psychotherapy',
  A7: 'Psychiatric - Inpatient',
  A8: 'Psychiatric - Outpatient',
  AG: 'Skilled Nursing Care',
  AI: 'Substance Abuse',
  AJ: 'Alcoholism',
  AK: 'Drug Addiction',
  AL: 'Vision (Optometry)',
  BU: 'Obstetrical',
  BV: 'Obstetrical/Gynecological',
  DM: 'Durable Medical Equipment',
  UC: 'Urgent Care',
};

// Primary service types that should always be shown expanded
const PRIMARY_SERVICE_TYPES = ['30', '42'];

// Helper to get plan effective date from benefit (handles both raw Stedi and parsed formats)
const getBenefitPlanDate = (
  benefitsDateInfo?: StediBenefitInfo['benefitsDateInformation']
): string | null => {
  if (!benefitsDateInfo) return null;
  // Try raw format first, then parsed format
  return (
    benefitsDateInfo.plan ||
    benefitsDateInfo.planBegin ||
    benefitsDateInfo.benefit ||
    benefitsDateInfo.benefitBegin ||
    benefitsDateInfo.periodStart ||
    null
  );
};

// Benefits Section Component with collapsible support for non-primary service types
interface BenefitsSectionProps {
  groupedBenefits: Map<string, StediBenefitInfo[]>;
  categorizeBenefits: (benefits: StediBenefitInfo[]) => {
    deductibles: StediBenefitInfo[];
    outOfPocket: StediBenefitInfo[];
    coInsurance: StediBenefitInfo[];
    coPayment: StediBenefitInfo[];
    coverage: StediBenefitInfo[];
    other: StediBenefitInfo[];
  };
  getServiceTypeName: (code: string) => string;
  formatCurrency: (amount?: string | null) => string;
  formatPercent: (percent?: string | null) => string;
  formatDate: (dateStr?: string | null) => string;
}

const BenefitsSection: React.FC<BenefitsSectionProps> = ({
  groupedBenefits,
  categorizeBenefits,
  getServiceTypeName,
  formatCurrency,
  formatPercent,
  formatDate,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );

  const toggleSection = (code: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  // Filter and separate service types
  const allServiceTypes = Array.from(groupedBenefits.entries()).filter(
    ([code]) => {
      const benefits = groupedBenefits.get(code) || [];
      return benefits.some(
        b =>
          b.code !== 'L' &&
          b.code !== 'X' &&
          b.code !== 'U' &&
          b.code !== 'R' &&
          b.code !== 'D'
      );
    }
  );

  const primaryServiceTypes = allServiceTypes.filter(([code]) =>
    PRIMARY_SERVICE_TYPES.includes(code)
  );
  const otherServiceTypes = allServiceTypes.filter(
    ([code]) => !PRIMARY_SERVICE_TYPES.includes(code)
  );

  const renderBenefitsTable = (benefits: StediBenefitInfo[]) => {
    const categorized = categorizeBenefits(benefits);
    const hasContent =
      categorized.deductibles.length > 0 ||
      categorized.outOfPocket.length > 0 ||
      categorized.coInsurance.length > 0 ||
      categorized.coPayment.length > 0 ||
      categorized.coverage.length > 0;

    if (!hasContent) return null;

    const formatNetworkLabel = (indicator?: string): string => {
      if (!indicator) return 'Not set';
      if (indicator === 'Yes') return 'In network';
      if (indicator === 'No') return 'Out of network';
      return indicator;
    };

    const formatBenefitValue = (benefit: StediBenefitInfo): string => {
      const amount = benefit.benefitAmount
        ? formatCurrency(benefit.benefitAmount)
        : null;
      const percent = benefit.benefitPercent
        ? formatPercent(benefit.benefitPercent)
        : null;
      const value = amount || percent || '';
      if (!value) return '';
      const tq = benefit.timeQualifier?.toLowerCase();
      if (tq === 'remaining') return `${value} remaining`;
      if (tq === 'calendar year') return `${value} per calendar year`;
      if (tq === 'visit') return `${value} per visit`;
      if (tq === 'episode') return `${value} per episode`;
      if (tq) return `${value} ${tq}`;
      return value;
    };

    type BenefitSubGroup = {
      coverageLevel: string;
      networkIndicator: string;
      benefits: StediBenefitInfo[];
    };

    const subGroupBenefits = (items: StediBenefitInfo[]): BenefitSubGroup[] => {
      const groupMap = new Map<string, StediBenefitInfo[]>();
      items.forEach(b => {
        const key = `${b.coverageLevelCode || ''}::${b.inPlanNetworkIndicatorCode || ''}`;
        const existing = groupMap.get(key) || [];
        existing.push(b);
        groupMap.set(key, existing);
      });

      const sortOrder = (key: string) => {
        const [level, network] = key.split('::');
        let score = 0;
        if (level === 'IND') score += 0;
        else if (level === 'FAM') score += 10;
        else score += 20;
        if (network === 'Y') score += 0;
        else if (network === 'N') score += 1;
        else score += 2;
        return score;
      };

      return Array.from(groupMap.entries())
        .sort(([a], [b]) => sortOrder(a) - sortOrder(b))
        .map(([_, groupItems]) => ({
          coverageLevel: groupItems[0].coverageLevel || '--',
          networkIndicator: formatNetworkLabel(
            groupItems[0].inPlanNetworkIndicator
          ),
          benefits: groupItems,
        }));
    };

    const renderCoverageValue = (benefit: StediBenefitInfo) => {
      const isActiveStatus = ['1', '2', '3', '4', '5'].includes(
        benefit.code || ''
      );
      const isInactiveStatus = ['6', '7', '8'].includes(benefit.code || '');

      if (isActiveStatus) {
        return (
          <div className='flex items-center gap-1 text-sm'>
            <CheckCircle className='h-3 w-3 text-emerald-500' />
            {benefit.name || 'Active coverage'}
          </div>
        );
      }

      if (isInactiveStatus) {
        return (
          <div className='flex items-center gap-1 text-sm'>
            <XCircle className='h-3 w-3 text-orange-500' />
            {benefit.name || 'Inactive'}
          </div>
        );
      }

      const value = formatBenefitValue(benefit);
      if (value) {
        return <div className='text-sm font-medium'>{value}</div>;
      }

      return <span className='text-muted-foreground text-xs italic'>--</span>;
    };

    const renderBenefitDetails = (benefit: StediBenefitInfo) => {
      const planDate = getBenefitPlanDate(benefit.benefitsDateInformation);
      const messages = benefit.additionalInformation
        ?.map(i => i.description)
        .filter(Boolean);
      const planCoverageDesc = benefit.planCoverage
        ? [
            benefit.planCoverage,
            benefit.benefitsAdditionalInformation?.planNetworkDescription,
          ]
            .filter(Boolean)
            .join(' - ')
        : benefit.benefitsAdditionalInformation?.planNetworkDescription;

      const hasDetails =
        planDate ||
        (messages && messages.length > 0) ||
        benefit.insuranceType ||
        planCoverageDesc;

      if (!hasDetails) {
        return <span className='text-muted-foreground text-xs italic'>--</span>;
      }

      return (
        <div className='space-y-1 text-sm'>
          {planDate && (
            <div>
              <span className='text-muted-foreground text-xs'>
                Plan effective
              </span>{' '}
              <span>{formatDate(planDate)}</span>
            </div>
          )}
          {messages && messages.length > 0 && (
            <div>
              <span className='text-muted-foreground text-xs'>
                {messages.length === 1 ? 'Message' : 'Messages'}
              </span>{' '}
              <span>{messages.join(', ')}</span>
            </div>
          )}
          {benefit.insuranceType && (
            <div>
              <span className='text-muted-foreground text-xs'>
                Insurance type
              </span>{' '}
              <span>{benefit.insuranceType}</span>
            </div>
          )}
          {planCoverageDesc && (
            <div>
              <span className='text-muted-foreground text-xs'>
                Plan coverage description
              </span>{' '}
              <span>{planCoverageDesc}</span>
            </div>
          )}
        </div>
      );
    };

    const renderCategoryRows = (
      categoryName: string,
      items: StediBenefitInfo[]
    ) => {
      if (items.length === 0) return null;
      const groups = subGroupBenefits(items);

      const totalRows = groups.reduce((sum, g) => sum + g.benefits.length, 0);
      let isFirstRow = true;

      return groups.flatMap((group, groupIdx) =>
        group.benefits.map((benefit, benefitIdx) => {
          const showCategory = isFirstRow;
          const showGroup = benefitIdx === 0;
          if (isFirstRow) isFirstRow = false;

          return (
            <TableRow key={`${categoryName}-${groupIdx}-${benefitIdx}`}>
              {showCategory && (
                <TableCell
                  className='font-medium align-top'
                  rowSpan={totalRows}
                >
                  {categoryName}
                </TableCell>
              )}
              {showGroup && (
                <>
                  <TableCell
                    className='text-muted-foreground align-top'
                    rowSpan={group.benefits.length}
                  >
                    {group.coverageLevel}
                  </TableCell>
                  <TableCell
                    className='text-muted-foreground align-top'
                    rowSpan={group.benefits.length}
                  >
                    {group.networkIndicator}
                  </TableCell>
                </>
              )}
              <TableCell className='align-top'>
                {renderCoverageValue(benefit)}
              </TableCell>
              <TableCell className='align-top'>
                {renderBenefitDetails(benefit)}
              </TableCell>
            </TableRow>
          );
        })
      );
    };

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-[120px]'>Type</TableHead>
            <TableHead className='w-[100px]'>Coverage level</TableHead>
            <TableHead className='w-[120px]'>Network indicator</TableHead>
            <TableHead>Coverage</TableHead>
            <TableHead>Benefit</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {renderCategoryRows('Statuses', categorized.coverage)}
          {renderCategoryRows('Co-insurance', categorized.coInsurance)}
          {renderCategoryRows('Deductible', categorized.deductibles)}
          {renderCategoryRows('Out of Pocket', categorized.outOfPocket)}
          {renderCategoryRows('Co-payment', categorized.coPayment)}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className='space-y-6'>
      <h3 className='text-lg font-semibold flex items-center gap-2'>
        <CreditCard className='h-5 w-5 text-emerald-600' />
        Benefits Information
      </h3>

      {/* Primary Service Types (30, 42) - Always expanded */}
      {primaryServiceTypes.map(([serviceTypeCode, benefits]) => {
        const table = renderBenefitsTable(benefits);
        if (!table) return null;

        return (
          <div
            key={serviceTypeCode}
            className='border rounded-lg overflow-hidden'
          >
            <div className='px-4 py-3 bg-muted/50 border-b'>
              <div className='flex items-center gap-2'>
                <Badge variant='outline' className='font-mono'>
                  STC {serviceTypeCode}
                </Badge>
                <span className='font-medium'>
                  {getServiceTypeName(serviceTypeCode)}
                </span>
              </div>
            </div>
            {table}
          </div>
        );
      })}

      {/* Other Service Types - Collapsible */}
      {otherServiceTypes.length > 0 && (
        <div className='space-y-2'>
          <h4 className='text-sm font-medium text-muted-foreground'>
            Other Service Types
          </h4>
          {otherServiceTypes.map(([serviceTypeCode, benefits]) => {
            const table = renderBenefitsTable(benefits);
            if (!table) return null;

            const isExpanded = expandedSections.has(serviceTypeCode);
            const DISPLAYED_CODES = [
              '1',
              '2',
              '3',
              '4',
              '5',
              '6',
              '7',
              '8',
              'A',
              'B',
              'C',
              'G',
            ];
            const totalBenefits = benefits.filter(b =>
              DISPLAYED_CODES.includes(b.code || '')
            ).length;

            return (
              <div
                key={serviceTypeCode}
                className='border rounded-lg overflow-hidden'
              >
                <button
                  onClick={() => toggleSection(serviceTypeCode)}
                  className='w-full px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors flex items-center justify-between text-left'
                >
                  <div className='flex items-center gap-2'>
                    {isExpanded ? (
                      <ChevronDown className='h-4 w-4 text-muted-foreground' />
                    ) : (
                      <ChevronRight className='h-4 w-4 text-muted-foreground' />
                    )}
                    <Badge variant='outline' className='font-mono'>
                      STC {serviceTypeCode}
                    </Badge>
                    <span className='font-medium'>
                      {getServiceTypeName(serviceTypeCode)}
                    </span>
                  </div>
                  <span className='text-xs text-muted-foreground'>
                    {totalBenefits} benefit{totalBenefits !== 1 ? 's' : ''}
                  </span>
                </button>
                {isExpanded && table}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EligibilityDetailsModal: React.FC<EligibilityDetailsModalProps> = ({
  isOpen,
  onClose,
  eligibilityResult,
}) => {
  const hasPermission = useStore(state => state.hasPermission);

  if (!eligibilityResult) return null;

  const rawResponse = eligibilityResult.raw_response as StediRawResponse | null;
  const isAdmin = hasPermission(PermissionSlug.ADMIN_SUPER_USER);

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return 'N/A';
    // Handle YYYYMMDD format
    if (
      dateStr.length === 8 &&
      !dateStr.includes('-') &&
      !dateStr.includes('/')
    ) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${month}/${day}/${year}`;
    }
    // Handle date range format YYYYMMDD-YYYYMMDD
    if (dateStr.includes('-') && dateStr.length === 17) {
      const [start, end] = dateStr.split('-');
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    // Handle ISO format
    if (dateStr.includes('T') || dateStr.includes('-')) {
      const parsed = parseISOToLocalDate(dateStr);
      return parsed ? parsed.toLocaleDateString() : dateStr;
    }
    return dateStr;
  };

  const formatCurrency = (amount?: string | null): string => {
    if (!amount) return '';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (percent?: string | null): string => {
    if (!percent) return '';
    const num = parseFloat(percent);
    if (isNaN(num)) return percent;
    return `${(num * 100).toFixed(0)}%`;
  };

  // Group benefits by service type code for primary display
  const groupBenefitsByServiceType = (
    benefits?: StediBenefitInfo[]
  ): Map<string, StediBenefitInfo[]> => {
    const grouped = new Map<string, StediBenefitInfo[]>();
    if (!benefits) return grouped;

    benefits.forEach(benefit => {
      const codes = benefit.serviceTypeCodes || [];
      if (codes.length === 0) {
        const existing = grouped.get('GENERAL') || [];
        existing.push(benefit);
        grouped.set('GENERAL', existing);
      } else {
        // Always group under primary service types when present in the codes list
        const matchedPrimary = codes.filter(c =>
          PRIMARY_SERVICE_TYPES.includes(c)
        );
        const nonPrimaryCodes = codes.filter(
          c => !PRIMARY_SERVICE_TYPES.includes(c)
        );

        for (const code of matchedPrimary) {
          const existing = grouped.get(code) || [];
          existing.push(benefit);
          grouped.set(code, existing);
        }

        if (nonPrimaryCodes.length > 0) {
          const firstCode = nonPrimaryCodes[0];
          const existing = grouped.get(firstCode) || [];
          existing.push(benefit);
          grouped.set(firstCode, existing);
        }
      }
    });

    return grouped;
  };

  // Filter and categorize benefits for display
  const categorizeBenefits = (
    benefits: StediBenefitInfo[]
  ): {
    deductibles: StediBenefitInfo[];
    outOfPocket: StediBenefitInfo[];
    coInsurance: StediBenefitInfo[];
    coPayment: StediBenefitInfo[];
    coverage: StediBenefitInfo[];
    other: StediBenefitInfo[];
  } => {
    return benefits.reduce(
      (acc, benefit) => {
        switch (benefit.code) {
          case 'C':
            acc.deductibles.push(benefit);
            break;
          case 'G':
            acc.outOfPocket.push(benefit);
            break;
          case 'A':
            acc.coInsurance.push(benefit);
            break;
          case 'B':
            acc.coPayment.push(benefit);
            break;
          case '1':
          case '2':
          case '3':
          case '4':
          case '5':
          case '6':
          case '7':
          case '8':
            acc.coverage.push(benefit);
            break;
          default:
            acc.other.push(benefit);
        }
        return acc;
      },
      {
        deductibles: [] as StediBenefitInfo[],
        outOfPocket: [] as StediBenefitInfo[],
        coInsurance: [] as StediBenefitInfo[],
        coPayment: [] as StediBenefitInfo[],
        coverage: [] as StediBenefitInfo[],
        other: [] as StediBenefitInfo[],
      }
    );
  };

  const getServiceTypeName = (code: string): string => {
    if (code === 'GENERAL') return 'General Information';
    return SERVICE_TYPE_NAMES[code] || `Service Type ${code}`;
  };

  const groupedBenefits = groupBenefitsByServiceType(
    rawResponse?.benefitsInformation
  );

  // Get primary care provider info
  const pcpBenefit = rawResponse?.benefitsInformation?.find(
    b => b.code === 'L' && b.name === 'Primary Care Provider'
  );
  const pcpEntity = pcpBenefit?.benefitsRelatedEntity;

  // Get health care facility info
  const facilityBenefit = rawResponse?.benefitsInformation?.find(
    b => b.code === 'X' && b.name === 'Health Care Facility'
  );
  const facilityEntity = facilityBenefit?.benefitsRelatedEntity;

  // Get other payor info
  const otherPayorBenefits = rawResponse?.benefitsInformation?.filter(
    b => b.code === 'R'
  );

  // Get contact entities
  const contactEntities = rawResponse?.benefitsInformation?.filter(
    b => b.code === 'U'
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-7xl max-h-[90vh] overflow-hidden flex flex-col'>
        <DialogHeader className='border-b pb-4'>
          <DialogTitle className='text-xl font-semibold'>
            Eligibility Check Details
          </DialogTitle>
          <div className='mt-2 text-sm text-muted-foreground'>
            Requested on{' '}
            {formatDateTimeInAgencyZone(eligibilityResult.request_date)}
          </div>
          {isAdmin && (
            <div className='mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono'>
              <span>
                <span className='font-sans font-medium text-muted-foreground'>
                  Check ID:
                </span>{' '}
                {eligibilityResult.id}
              </span>
              {eligibilityResult.request_id && (
                <span>
                  <span className='font-sans font-medium text-muted-foreground'>
                    Stedi Request ID:
                  </span>{' '}
                  {eligibilityResult.request_id}
                </span>
              )}
              {eligibilityResult.parent_eligibility_check_id && (
                <span>
                  <span className='font-sans font-medium text-muted-foreground'>
                    Parent Check ID:
                  </span>{' '}
                  {eligibilityResult.parent_eligibility_check_id}
                </span>
              )}
            </div>
          )}
        </DialogHeader>

        <Tabs
          defaultValue='overview'
          className='flex-1 flex flex-col overflow-hidden'
        >
          <div className='flex items-center justify-between mx-4 mt-2'>
            <TabsList className='w-fit'>
              <TabsTrigger value='overview'>Overview</TabsTrigger>
              <TabsTrigger value='benefits'>Benefits</TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent
            value='overview'
            className='flex-1 overflow-y-auto px-4 pb-4 mt-0'
          >
            <div className='space-y-6 py-4'>
              {/* Subscriber Section */}
              {rawResponse?.subscriber && (
                <div className='border rounded-lg overflow-hidden'>
                  <div className='px-4 py-3 bg-muted/50 border-b'>
                    <h3 className='font-semibold flex items-center gap-2'>
                      <User className='h-4 w-4 text-blue-600' />
                      Subscriber
                    </h3>
                  </div>
                  <div className='p-4 space-y-4'>
                    {/* Name Row */}
                    <div className='grid grid-cols-2 md:grid-cols-6 gap-4 text-sm'>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          First name
                        </div>
                        <div className='font-medium'>
                          {rawResponse.subscriber.firstName || '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Middle name
                        </div>
                        <div className='font-medium'>
                          {rawResponse.subscriber.middleName || '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Last name
                        </div>
                        <div className='font-medium'>
                          {rawResponse.subscriber.lastName || '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Date of birth
                        </div>
                        <div className='font-medium'>
                          {rawResponse.subscriber.dateOfBirth
                            ? formatDate(rawResponse.subscriber.dateOfBirth)
                            : '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Gender
                        </div>
                        <div className='font-medium'>
                          {rawResponse.subscriber.gender === 'M'
                            ? 'Male'
                            : rawResponse.subscriber.gender === 'F'
                              ? 'Female'
                              : rawResponse.subscriber.gender || '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Member ID
                        </div>
                        <div className='font-medium'>
                          {rawResponse.subscriber.memberId || '—'}
                        </div>
                      </div>
                    </div>
                    {/* Group Number Row */}
                    <div className='grid grid-cols-2 md:grid-cols-6 gap-4 text-sm'>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Group number
                        </div>
                        <div className='font-medium'>
                          {rawResponse.subscriber.groupNumber ||
                            rawResponse?.planInformation?.groupNumber ||
                            '—'}
                        </div>
                      </div>
                    </div>
                    {/* Address Section */}
                    {rawResponse.subscriber.address && (
                      <div className='border-t pt-4'>
                        <div className='text-muted-foreground text-xs mb-2 font-medium uppercase'>
                          Address
                        </div>
                        <div className='grid grid-cols-2 md:grid-cols-5 gap-4 text-sm'>
                          <div>
                            <div className='text-muted-foreground text-xs mb-1'>
                              Line 1
                            </div>
                            <div className='font-medium text-blue-600'>
                              {rawResponse.subscriber.address.address1 || '—'}
                            </div>
                          </div>
                          <div>
                            <div className='text-muted-foreground text-xs mb-1'>
                              Line 2
                            </div>
                            <div className='font-medium'>—</div>
                          </div>
                          <div>
                            <div className='text-muted-foreground text-xs mb-1'>
                              City
                            </div>
                            <div className='font-medium'>
                              {rawResponse.subscriber.address.city || '—'}
                            </div>
                          </div>
                          <div>
                            <div className='text-muted-foreground text-xs mb-1'>
                              State
                            </div>
                            <div className='font-medium'>
                              {rawResponse.subscriber.address.state || '—'}
                            </div>
                          </div>
                          <div>
                            <div className='text-muted-foreground text-xs mb-1'>
                              Postal code
                            </div>
                            <div className='font-medium'>
                              {rawResponse.subscriber.address.postalCode || '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Encounter Section */}
              {(rawResponse?.planStatus?.[0]?.serviceTypeCodes ||
                rawResponse?.encounter?.serviceTypeCodes) && (
                <div className='border rounded-lg overflow-hidden'>
                  <div className='px-4 py-3 bg-muted/50 border-b'>
                    <h3 className='font-semibold'>Encounter</h3>
                  </div>
                  <div className='p-4'>
                    <div className='text-muted-foreground text-xs mb-2'>
                      Service type codes
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      {(
                        rawResponse?.planStatus?.[0]?.serviceTypeCodes ||
                        rawResponse?.encounter?.serviceTypeCodes ||
                        []
                      ).map(code => (
                        <Badge
                          key={code}
                          variant='secondary'
                          className='font-mono'
                        >
                          {code}: {getServiceTypeName(code)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Provider Section */}
              {rawResponse?.provider && (
                <div className='border rounded-lg overflow-hidden'>
                  <div className='px-4 py-3 bg-muted/50 border-b'>
                    <h3 className='font-semibold flex items-center gap-2'>
                      <Stethoscope className='h-4 w-4 text-emerald-600' />
                      Provider
                    </h3>
                  </div>
                  <div className='p-4'>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Organization name
                        </div>
                        <div className='font-medium'>
                          {rawResponse.provider.organizationName ||
                            rawResponse.provider.providerName ||
                            '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          NPI
                        </div>
                        <div className='font-medium'>
                          {rawResponse.provider.npi || '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payer Section */}
              {rawResponse?.payer && (
                <div className='border rounded-lg overflow-hidden'>
                  <div className='px-4 py-3 bg-muted/50 border-b'>
                    <h3 className='font-semibold flex items-center gap-2'>
                      <Building2 className='h-4 w-4 text-purple-600' />
                      Payer
                    </h3>
                  </div>
                  <div className='p-4'>
                    <div className='grid grid-cols-3 gap-4 text-sm'>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Organization name
                        </div>
                        <div className='font-medium'>
                          {rawResponse.payer.name || '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          ID
                        </div>
                        <div className='font-medium'>
                          {rawResponse.payer.payorIdentification || '—'}
                        </div>
                      </div>
                      <div>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Contact URL
                        </div>
                        <div className='font-medium'>
                          {rawResponse.payer.contactInformation?.contacts?.find(
                            c =>
                              c.communicationMode ===
                              'Uniform Resource Locator (URL)'
                          )?.communicationNumber ? (
                            <a
                              href={`https://${rawResponse.payer.contactInformation.contacts.find(c => c.communicationMode === 'Uniform Resource Locator (URL)')?.communicationNumber}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-600 hover:underline'
                            >
                              {
                                rawResponse.payer.contactInformation.contacts.find(
                                  c =>
                                    c.communicationMode ===
                                    'Uniform Resource Locator (URL)'
                                )?.communicationNumber
                              }
                            </a>
                          ) : (
                            '—'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Section */}
              <div className='border rounded-lg overflow-hidden'>
                <div className='px-4 py-3 bg-muted/50 border-b'>
                  <h3 className='font-semibold flex items-center gap-2'>
                    <Calendar className='h-4 w-4 text-amber-600' />
                    Plan
                  </h3>
                </div>
                <div className='p-4'>
                  <div className='grid grid-cols-2 md:grid-cols-6 gap-4 text-sm'>
                    <div className='md:col-span-2'>
                      <div className='text-muted-foreground text-xs mb-1'>
                        Plan names
                      </div>
                      <div className='font-medium'>
                        {eligibilityResult.plan_name ||
                          rawResponse?.planStatus?.[0]?.planDetails ||
                          // Fallback: get planCoverage from Health Benefit Plan Coverage benefit
                          rawResponse?.benefitsInformation?.find(
                            b =>
                              b.serviceTypes?.includes(
                                'Health Benefit Plan Coverage'
                              ) && b.planCoverage
                          )?.planCoverage ||
                          '—'}
                      </div>
                    </div>
                    <div>
                      <div className='text-muted-foreground text-xs mb-1'>
                        Plan number
                      </div>
                      <div className='font-medium'>
                        {rawResponse?.planInformation?.planNumber ||
                          rawResponse?.subscriber?.planNumber ||
                          '—'}
                      </div>
                    </div>
                    <div>
                      <div className='text-muted-foreground text-xs mb-1'>
                        Group number
                      </div>
                      <div className='font-medium'>
                        {rawResponse?.planInformation?.groupNumber ||
                          rawResponse?.subscriber?.groupNumber ||
                          '—'}
                      </div>
                    </div>
                    <div>
                      <div className='text-muted-foreground text-xs mb-1'>
                        Group description
                      </div>
                      <div className='font-medium'>
                        {rawResponse?.planInformation?.groupDescription ||
                          rawResponse?.subscriber?.groupDescription ||
                          '—'}
                      </div>
                    </div>
                  </div>
                  {/* Insurance types as list - extracted from benefits */}
                  {(() => {
                    const insuranceTypes = [
                      ...new Set(
                        rawResponse?.benefitsInformation
                          ?.filter(b => b.insuranceType && b.code === '1')
                          .map(b => b.insuranceType) || []
                      ),
                    ];
                    return insuranceTypes.length > 0 ? (
                      <div className='mt-4'>
                        <div className='text-muted-foreground text-xs mb-1'>
                          Insurance types
                        </div>
                        <ul className='list-disc list-inside space-y-1'>
                          {insuranceTypes.map((type, idx) => (
                            <li key={idx} className='font-medium text-sm'>
                              {type}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null;
                  })()}
                  <div className='grid grid-cols-2 md:grid-cols-6 gap-4 text-sm mt-4'>
                    <div className='md:col-span-2'>
                      {(() => {
                        // Check for actual plan dates first (not eligibility)
                        const planDateInfo = rawResponse?.planDateInformation;
                        const actualPlanDate =
                          planDateInfo?.plan || planDateInfo?.planBegin;
                        const eligibilityDate =
                          planDateInfo?.eligibility ||
                          planDateInfo?.eligibilityBegin;

                        if (actualPlanDate) {
                          return (
                            <>
                              <div className='text-muted-foreground text-xs mb-1'>
                                Plan date
                              </div>
                              <div className='font-medium'>
                                {formatDate(actualPlanDate)}
                              </div>
                            </>
                          );
                        } else if (
                          eligibilityResult.effective_date &&
                          eligibilityResult.termination_date
                        ) {
                          return (
                            <>
                              <div className='text-muted-foreground text-xs mb-1'>
                                Plan date
                              </div>
                              <div className='font-medium'>
                                {formatDate(eligibilityResult.effective_date)}—
                                {formatDate(eligibilityResult.termination_date)}
                              </div>
                            </>
                          );
                        } else if (eligibilityResult.effective_date) {
                          return (
                            <>
                              <div className='text-muted-foreground text-xs mb-1'>
                                Plan date
                              </div>
                              <div className='font-medium'>
                                {formatDate(eligibilityResult.effective_date)}
                              </div>
                            </>
                          );
                        } else if (eligibilityDate) {
                          return (
                            <>
                              <div className='text-muted-foreground text-xs mb-1'>
                                Eligibility
                              </div>
                              <div className='font-medium'>
                                Valid as of: {formatDate(eligibilityDate)}
                              </div>
                            </>
                          );
                        } else {
                          return (
                            <>
                              <div className='text-muted-foreground text-xs mb-1'>
                                Plan date
                              </div>
                              <div className='font-medium'>—</div>
                            </>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefit - related entities Section */}
              {(rawResponse?.payer ||
                pcpEntity ||
                contactEntities?.length ||
                otherPayorBenefits?.length ||
                facilityEntity) && (
                <div className='border rounded-lg overflow-hidden'>
                  <div className='px-4 py-3 bg-muted/50 border-b'>
                    <h3 className='font-semibold'>
                      Benefit - related entities
                    </h3>
                  </div>
                  <div className='overflow-x-auto'>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className='min-w-[200px]'>
                            Entity
                          </TableHead>
                          <TableHead className='min-w-[200px]'>
                            Contact
                          </TableHead>
                          <TableHead className='min-w-[200px]'>
                            Address
                          </TableHead>
                          <TableHead className='min-w-[200px]'>
                            Associated benefits
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Payer Row */}
                        {rawResponse?.payer && (
                          <TableRow>
                            <TableCell>
                              <div className='font-medium'>
                                {rawResponse.payer.name}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Payer
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Active Coverage
                              </div>
                            </TableCell>
                            <TableCell>
                              {rawResponse.payer.contactInformation?.contacts?.map(
                                (contact, idx) => (
                                  <div
                                    key={idx}
                                    className='flex items-center gap-1 text-sm'
                                  >
                                    {contact.communicationMode ===
                                    'Uniform Resource Locator (URL)' ? (
                                      <a
                                        href={`https://${contact.communicationNumber}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-blue-600 hover:underline flex items-center gap-1'
                                      >
                                        <Globe className='h-3 w-3' />
                                        {contact.communicationNumber?.toUpperCase()}
                                      </a>
                                    ) : contact.communicationMode ===
                                      'Telephone' ? (
                                      <span className='flex items-center gap-1'>
                                        <Phone className='h-3 w-3' />
                                        {contact.communicationNumber}
                                      </span>
                                    ) : (
                                      contact.communicationNumber
                                    )}
                                  </div>
                                )
                              )}
                            </TableCell>
                            <TableCell className='text-sm text-muted-foreground'>
                              —
                            </TableCell>
                            <TableCell>
                              <Badge variant='outline' className='font-mono'>
                                30 Health Benefit Plan Coverage
                              </Badge>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Primary Care Provider Row */}
                        {pcpEntity && (
                          <TableRow>
                            <TableCell>
                              <div className='font-medium'>
                                {[
                                  pcpEntity.entityFirstname,
                                  pcpEntity.entityMiddlename,
                                  pcpEntity.entityName,
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Primary Care Provider
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Primary Care Provider
                              </div>
                            </TableCell>
                            <TableCell>
                              {pcpEntity.contactInformation?.contacts?.map(
                                (contact, idx) => (
                                  <div key={idx} className='text-sm'>
                                    {contact.communicationMode ===
                                      'Telephone' && (
                                      <div className='flex items-center gap-1 text-blue-600'>
                                        <Phone className='h-3 w-3' />
                                        {contact.communicationNumber}
                                      </div>
                                    )}
                                    {contact.communicationMode ===
                                      'Facsimile' && (
                                      <div className='flex items-center gap-1 text-muted-foreground'>
                                        <span className='text-xs'>📠</span>
                                        {contact.communicationNumber}
                                      </div>
                                    )}
                                  </div>
                                )
                              )}
                            </TableCell>
                            <TableCell className='text-sm'>
                              {pcpEntity.address ? (
                                <div>
                                  <div>{pcpEntity.address.address1}</div>
                                  <div className='text-muted-foreground'>
                                    {[
                                      pcpEntity.address.city,
                                      pcpEntity.address.state,
                                      pcpEntity.address.postalCode,
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                  </div>
                                </div>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className='text-muted-foreground'>
                              —
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Contact Entities Rows */}
                        {contactEntities?.map((benefit, idx) => (
                          <TableRow key={`contact-${idx}`}>
                            <TableCell>
                              <div className='font-medium'>
                                {benefit.benefitsRelatedEntity?.entityName}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Vendor
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Contact Following Entity for Eligibility or
                                Benefit Information
                              </div>
                            </TableCell>
                            <TableCell>
                              {benefit.benefitsRelatedEntity?.contactInformation?.contacts?.map(
                                (contact, i) => (
                                  <div key={i} className='text-sm'>
                                    {contact.communicationMode ===
                                    'Uniform Resource Locator (URL)' ? (
                                      <a
                                        href={
                                          contact.communicationNumber?.startsWith(
                                            'http'
                                          )
                                            ? contact.communicationNumber
                                            : `https://${contact.communicationNumber}`
                                        }
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-blue-600 hover:underline flex items-center gap-1'
                                      >
                                        <Globe className='h-3 w-3' />
                                        {contact.communicationNumber?.toUpperCase()}
                                      </a>
                                    ) : contact.communicationMode ===
                                      'Telephone' ? (
                                      <span className='flex items-center gap-1'>
                                        <Phone className='h-3 w-3' />
                                        {contact.communicationNumber}
                                      </span>
                                    ) : (
                                      contact.communicationNumber
                                    )}
                                  </div>
                                )
                              )}
                            </TableCell>
                            <TableCell className='text-muted-foreground'>
                              —
                            </TableCell>
                            <TableCell>
                              {benefit.serviceTypeCodes?.map(code => (
                                <Badge
                                  key={code}
                                  variant='outline'
                                  className='font-mono mr-1'
                                >
                                  {code} {getServiceTypeName(code)}
                                </Badge>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Health Care Facility Row */}
                        {facilityEntity && (
                          <TableRow>
                            <TableCell>
                              <div className='font-medium'>
                                {facilityEntity.entityName}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Provider
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                Health Care Facility
                              </div>
                            </TableCell>
                            <TableCell className='text-muted-foreground'>
                              —
                            </TableCell>
                            <TableCell className='text-muted-foreground'>
                              —
                            </TableCell>
                            <TableCell className='text-muted-foreground'>
                              —
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Other Payor Rows (from benefitsRelatedEntity or benefitsRelatedEntities) */}
                        {otherPayorBenefits?.map((benefit, idx) => {
                          // Use benefitsRelatedEntity or first item from benefitsRelatedEntities
                          const entity =
                            benefit.benefitsRelatedEntity ||
                            benefit.benefitsRelatedEntities?.[0];
                          if (!entity) return null;

                          return (
                            <TableRow key={`other-payor-overview-${idx}`}>
                              <TableCell>
                                <div className='font-medium'>
                                  {entity.entityName}
                                </div>
                                <div className='text-xs text-muted-foreground'>
                                  {entity.entityIdentifier || 'Payer'}
                                </div>
                                <div className='text-xs text-muted-foreground'>
                                  Other or Additional Payor
                                </div>
                              </TableCell>
                              <TableCell>
                                {entity.contactInformation?.contacts?.map(
                                  (contact, i) => (
                                    <div key={i} className='text-sm'>
                                      {contact.communicationMode ===
                                      'Telephone' ? (
                                        <a
                                          href={`tel:${contact.communicationNumber}`}
                                          className='text-blue-600 hover:underline flex items-center gap-1'
                                        >
                                          <Phone className='h-3 w-3' />
                                          {contact.communicationNumber?.replace(
                                            /(\d{3})(\d{3})(\d{4})/,
                                            '($1) $2-$3'
                                          )}
                                        </a>
                                      ) : contact.communicationMode ===
                                        'Uniform Resource Locator (URL)' ? (
                                        <a
                                          href={
                                            contact.communicationNumber?.startsWith(
                                              'http'
                                            )
                                              ? contact.communicationNumber
                                              : `https://${contact.communicationNumber}`
                                          }
                                          target='_blank'
                                          rel='noopener noreferrer'
                                          className='text-muted-foreground hover:underline flex items-center gap-1'
                                        >
                                          <Globe className='h-3 w-3' />
                                          {contact.communicationNumber}
                                        </a>
                                      ) : (
                                        contact.communicationNumber
                                      )}
                                    </div>
                                  )
                                )}
                              </TableCell>
                              <TableCell className='text-sm'>
                                {entity.address ? (
                                  <div>
                                    <div>{entity.address.address1}</div>
                                    <div className='text-muted-foreground'>
                                      {[
                                        entity.address.city,
                                        entity.address.state,
                                        entity.address.postalCode,
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    </div>
                                  </div>
                                ) : (
                                  <span className='text-muted-foreground'>
                                    —
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {benefit.serviceTypeCodes?.map(code => (
                                  <Badge
                                    key={code}
                                    variant='outline'
                                    className='font-mono mr-1'
                                  >
                                    {code} {getServiceTypeName(code)}
                                  </Badge>
                                ))}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Error Message - User-friendly display */}
              {(() => {
                const formattedError = formatEligibilityErrorForDisplay(
                  eligibilityResult.error_message,
                  rawResponse?.errors as StediAAAError[] | null
                );
                if (!formattedError) return null;
                return (
                  <div className='space-y-3'>
                    <h3 className='text-lg font-semibold flex items-center gap-2 text-destructive'>
                      <AlertTriangle className='h-5 w-5' />
                      Error Message
                    </h3>
                    <div className='bg-destructive/10 border border-destructive/20 rounded p-4 space-y-2'>
                      <div className='text-sm text-destructive font-medium'>
                        {formattedError.message}
                      </div>
                      {formattedError.suggestion && (
                        <div className='text-sm text-destructive/80'>
                          {formattedError.suggestion}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          {/* Benefits Tab */}
          <TabsContent
            value='benefits'
            className='flex-1 overflow-y-auto px-4 pb-4 mt-0'
          >
            <div className='space-y-6 py-4'>
              {/* Plan Coverage Status */}
              {rawResponse?.planStatus && rawResponse.planStatus.length > 0 && (
                <div className='space-y-3'>
                  <h3 className='text-lg font-semibold flex items-center gap-2'>
                    <CheckCircle className='h-5 w-5 text-emerald-600' />
                    Plan Coverage Status
                  </h3>
                  <div className='grid gap-3'>
                    {rawResponse.planStatus.map((status, idx) => (
                      <div
                        key={idx}
                        className='bg-emerald-50 border border-emerald-200 rounded-lg p-4'
                      >
                        <div className='flex items-center gap-2 mb-2'>
                          <Badge variant='default' className='bg-emerald-600'>
                            {status.status}
                          </Badge>
                          {status.planDetails && (
                            <span className='text-sm font-medium text-emerald-800'>
                              {status.planDetails}
                            </span>
                          )}
                        </div>
                        {status.serviceTypeCodes &&
                          status.serviceTypeCodes.length > 0 && (
                            <div className='text-sm text-emerald-700'>
                              <span className='font-medium'>
                                Service Types:{' '}
                              </span>
                              {status.serviceTypeCodes
                                .map(code => getServiceTypeName(code))
                                .join(', ')}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits by Service Type */}
              {groupedBenefits.size > 0 && (
                <BenefitsSection
                  groupedBenefits={groupedBenefits}
                  categorizeBenefits={categorizeBenefits}
                  getServiceTypeName={getServiceTypeName}
                  formatCurrency={formatCurrency}
                  formatPercent={formatPercent}
                  formatDate={formatDate}
                />
              )}

              {/* Service not defined - entities without specific service type codes */}
              {(pcpBenefit ||
                facilityBenefit ||
                (otherPayorBenefits && otherPayorBenefits.length > 0)) && (
                <div className='border rounded-lg overflow-hidden'>
                  <div className='px-4 py-3 bg-muted/50 border-b'>
                    <span className='font-medium'>Service not defined</span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className='w-[150px]'>Type</TableHead>
                        <TableHead className='w-[120px]'>
                          Coverage level
                        </TableHead>
                        <TableHead className='w-[120px]'>
                          Network indicator
                        </TableHead>
                        <TableHead>Benefit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Primary Care Provider */}
                      {pcpBenefit && pcpEntity && (
                        <TableRow>
                          <TableCell className='font-medium align-top'>
                            Primary care provider
                          </TableCell>
                          <TableCell className='text-muted-foreground align-top'>
                            {pcpBenefit.coverageLevel || 'Individual'}
                          </TableCell>
                          <TableCell className='text-muted-foreground align-top'>
                            {pcpBenefit.inPlanNetworkIndicator || '--'}
                          </TableCell>
                          <TableCell>
                            <div className='space-y-3 text-sm'>
                              <div>
                                <div className='text-muted-foreground text-xs font-medium mb-1'>
                                  Related entity
                                </div>
                                <div>
                                  <span className='text-muted-foreground'>
                                    Type:
                                  </span>{' '}
                                  Primary care provider
                                </div>
                                <div>
                                  <span className='text-muted-foreground'>
                                    Entity:
                                  </span>{' '}
                                  {[
                                    pcpEntity.entityFirstname,
                                    pcpEntity.entityMiddlename,
                                    pcpEntity.entityName,
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                </div>
                                {pcpEntity.entityIdentificationValue && (
                                  <div>
                                    <span className='text-muted-foreground'>
                                      Identifier:
                                    </span>{' '}
                                    NPI {pcpEntity.entityIdentificationValue}
                                  </div>
                                )}
                                {pcpEntity.providerInformation
                                  ?.referenceIdentification && (
                                  <div>
                                    <span className='text-muted-foreground'>
                                      Provider:
                                    </span>{' '}
                                    Primary care physician{' '}
                                    {
                                      pcpEntity.providerInformation
                                        .referenceIdentification
                                    }
                                  </div>
                                )}
                              </div>
                              {pcpEntity.contactInformation?.contacts &&
                                pcpEntity.contactInformation.contacts.length >
                                  0 && (
                                  <div>
                                    <div className='text-muted-foreground text-xs font-medium mb-1'>
                                      CONTACT
                                    </div>
                                    {pcpEntity.contactInformation.contacts.map(
                                      (contact, idx) => (
                                        <div key={idx}>
                                          {contact.communicationMode ===
                                            'Telephone' && (
                                            <div>
                                              <span className='text-muted-foreground'>
                                                Phone:
                                              </span>{' '}
                                              {contact.communicationNumber}
                                            </div>
                                          )}
                                          {contact.communicationMode ===
                                            'Facsimile' && (
                                            <div>
                                              <span className='text-muted-foreground'>
                                                Fax:
                                              </span>{' '}
                                              {contact.communicationNumber}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                              {pcpEntity.address && (
                                <div>
                                  <div className='text-muted-foreground text-xs font-medium mb-1'>
                                    ADDRESS
                                  </div>
                                  <div>
                                    <span className='text-muted-foreground'>
                                      Street:
                                    </span>{' '}
                                    {pcpEntity.address.address1}
                                  </div>
                                  {pcpEntity.address.city && (
                                    <div>
                                      <span className='text-muted-foreground'>
                                        City:
                                      </span>{' '}
                                      {pcpEntity.address.city}
                                    </div>
                                  )}
                                  {pcpEntity.address.state && (
                                    <div>
                                      <span className='text-muted-foreground'>
                                        State:
                                      </span>{' '}
                                      {pcpEntity.address.state}
                                    </div>
                                  )}
                                  {pcpEntity.address.postalCode && (
                                    <div>
                                      <span className='text-muted-foreground'>
                                        Postal code:
                                      </span>{' '}
                                      {pcpEntity.address.postalCode}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Health Care Facility */}
                      {facilityBenefit && facilityEntity && (
                        <TableRow>
                          <TableCell className='font-medium align-top'>
                            Health care facility
                          </TableCell>
                          <TableCell className='text-muted-foreground align-top'>
                            {facilityBenefit.coverageLevel || 'Individual'}
                          </TableCell>
                          <TableCell className='text-muted-foreground align-top'>
                            {facilityBenefit.inPlanNetworkIndicator || '--'}
                          </TableCell>
                          <TableCell>
                            <div className='space-y-1 text-sm'>
                              <div className='text-muted-foreground text-xs font-medium mb-1'>
                                Related entity
                              </div>
                              <div>
                                <span className='text-muted-foreground'>
                                  Type:
                                </span>{' '}
                                Provider
                              </div>
                              <div>
                                <span className='text-muted-foreground'>
                                  Entity:
                                </span>{' '}
                                {facilityEntity.entityName}
                              </div>
                              {facilityEntity.entityIdentificationValue && (
                                <div>
                                  <span className='text-muted-foreground'>
                                    Identifier:
                                  </span>{' '}
                                  NPI {facilityEntity.entityIdentificationValue}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Other or Additional Payors */}
                      {otherPayorBenefits?.map((benefit, idx) => (
                        <TableRow key={`other-payor-${idx}`}>
                          <TableCell className='font-medium align-top'>
                            Other or additional payor
                          </TableCell>
                          <TableCell className='text-muted-foreground align-top'>
                            {benefit.coverageLevel || 'Individual'}
                          </TableCell>
                          <TableCell className='text-muted-foreground align-top'>
                            {benefit.inPlanNetworkIndicator || '--'}
                          </TableCell>
                          <TableCell>
                            <div className='space-y-1 text-sm'>
                              {benefit.benefitsAdditionalInformation
                                ?.hicNumber && (
                                <>
                                  <div className='text-muted-foreground text-xs font-medium mb-1'>
                                    Additional information
                                  </div>
                                  <div>
                                    <span className='text-muted-foreground'>
                                      MBI:
                                    </span>{' '}
                                    {
                                      benefit.benefitsAdditionalInformation
                                        .hicNumber
                                    }
                                  </div>
                                </>
                              )}
                              {benefit.benefitsRelatedEntity && (
                                <>
                                  <div className='text-muted-foreground text-xs font-medium mb-1'>
                                    Related entity
                                  </div>
                                  <div>
                                    <span className='text-muted-foreground'>
                                      Entity:
                                    </span>{' '}
                                    {benefit.benefitsRelatedEntity.entityName}
                                  </div>
                                  {benefit.benefitsRelatedEntity
                                    .entityIdentificationValue && (
                                    <div>
                                      <span className='text-muted-foreground'>
                                        Identifier:
                                      </span>{' '}
                                      {
                                        benefit.benefitsRelatedEntity
                                          .entityIdentificationValue
                                      }
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Error Message - User-friendly display */}
              {(() => {
                const formattedError = formatEligibilityErrorForDisplay(
                  eligibilityResult.error_message,
                  rawResponse?.errors as StediAAAError[] | null
                );
                if (!formattedError) return null;
                return (
                  <div className='space-y-3'>
                    <h3 className='text-lg font-semibold flex items-center gap-2 text-destructive'>
                      <AlertTriangle className='h-5 w-5' />
                      Error Message
                    </h3>
                    <div className='bg-destructive/10 border border-destructive/20 rounded p-4 space-y-2'>
                      <div className='text-sm text-destructive font-medium'>
                        {formattedError.message}
                      </div>
                      {formattedError.suggestion && (
                        <div className='text-sm text-destructive/80'>
                          {formattedError.suggestion}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* No Benefits Info Message */}
              {(!rawResponse?.benefitsInformation ||
                rawResponse.benefitsInformation.length === 0) &&
                !rawResponse?.errors?.length &&
                !eligibilityResult.error_message && (
                  <div className='py-8 text-center text-muted-foreground'>
                    No benefits information available in the response.
                  </div>
                )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default EligibilityDetailsModal;
